import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Headphones, FileText, Wrench, CheckCircle } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Headphones,
    title: "Contactez-nous",
    description: "Appelez-nous au 0465 19 57 26 ou remplissez notre formulaire en ligne pour nous expliquer votre problème.",
    details: "Notre équipe est disponible 24h/24 et 7j/7 pour répondre à vos urgences et prendre en charge votre demande rapidement."
  },
  {
    number: "02", 
    icon: FileText,
    title: "Devis Gratuit",
    description: "Nous établissons un devis détaillé et transparent après évaluation de vos besoins spécifiques.",
    details: "Nos techniciens se déplacent gratuitement pour évaluer les travaux et vous proposer une solution adaptée à votre budget."
  },
  {
    number: "03",
    icon: Wrench,
    title: "Intervention Rapide",
    description: "Nos techniciens certifiés interviennent rapidement chez vous avec tout l'équipement nécessaire.",
    details: "Intervention sous 2 heures en cas d'urgence. Nos véhicules sont équipés des outils et pièces les plus courantes."
  },
  {
    number: "04",
    icon: CheckCircle,
    title: "Satisfaction Garantie",
    description: "Travaux garantis 5 ans avec suivi de qualité et service après-vente assuré.",
    details: "Nous restons à votre disposition après l'intervention et garantissons tous nos travaux pour votre tranquillité d'esprit."
  }
];

export function HowToUse() {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  // Mouse position for magnetic effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 25, stiffness: 200 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  // Transform for parallax on the large number
  const numberX = useTransform(x, [-200, 200], [-20, 20]);
  const numberY = useTransform(y, [-200, 200], [-10, 10]);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      mouseX.set(e.clientX - centerX);
      mouseY.set(e.clientY - centerY);
    }
  };

  const goNext = () => setActiveIndex((prev) => (prev + 1) % steps.length);
  const goPrev = () => setActiveIndex((prev) => (prev - 1 + steps.length) % steps.length);

  useEffect(() => {
    const timer = setInterval(goNext, 8000);
    return () => clearInterval(timer);
  }, []);

  const current = steps[activeIndex];
  const IconComponent = current.icon;

  return (
    <div className="flex items-center justify-center min-h-[600px] bg-transparent overflow-hidden">
      <div ref={containerRef} className="relative w-full max-w-5xl px-4 sm:px-6 lg:px-8" onMouseMove={handleMouseMove}>
        {/* Oversized step number - positioned to bleed off left edge */}
        <motion.div
          className="absolute -left-8 top-1/2 -translate-y-1/2 text-[20rem] md:text-[28rem] font-bold text-blue-900/[0.03] select-none pointer-events-none leading-none tracking-tighter"
          style={{ x: numberX, y: numberY }}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="block"
            >
              {current.number}
            </motion.span>
          </AnimatePresence>
        </motion.div>

        {/* Main content - asymmetric layout */}
        <div className="relative flex flex-col lg:flex-row">
          {/* Left column - vertical text */}
          <div className="flex flex-row lg:flex-col items-center justify-center pb-8 lg:pb-0 lg:pr-16 border-b lg:border-b-0 lg:border-r border-gray-200">
            <motion.span
              className="text-xs font-mono text-gray-500 tracking-widest uppercase lg:rotate-90 lg:whitespace-nowrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Comment Ça Marche
            </motion.span>

            {/* Progress line */}
            <div className="relative w-32 h-px lg:w-px lg:h-32 bg-gray-200 ml-8 lg:ml-0 lg:mt-8">
              <motion.div
                className="absolute top-0 left-0 bg-blue-900 lg:origin-top origin-left"
                animate={{
                  width: window.innerWidth >= 1024 ? "100%" : `${((activeIndex + 1) / steps.length) * 100}%`,
                  height: window.innerWidth >= 1024 ? `${((activeIndex + 1) / steps.length) * 100}%` : "100%",
                }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>

          {/* Center - main content */}
          <div className="flex-1 pt-8 lg:pt-0 lg:pl-16 py-12">
            {/* Step badge with icon */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.4 }}
                className="mb-8"
              >
                <div className="inline-flex items-center gap-3 text-xs font-mono text-gray-500 border border-gray-200 rounded-full px-4 py-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-blue-600" />
                  </div>
                  <span>Étape {activeIndex + 1}/4</span>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Title with character reveal */}
            <div className="relative mb-8">
              <AnimatePresence mode="wait">
                <motion.h3
                  key={activeIndex}
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-900 leading-[1.15] tracking-tight mb-4"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {current.title.split(" ").map((word, i) => (
                    <motion.span
                      key={i}
                      className="inline-block mr-[0.3em]"
                      variants={{
                        hidden: { opacity: 0, y: 20, rotateX: 90 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          rotateX: 0,
                          transition: {
                            duration: 0.5,
                            delay: i * 0.1,
                            ease: [0.22, 1, 0.36, 1],
                          },
                        },
                        exit: {
                          opacity: 0,
                          y: -10,
                          transition: { duration: 0.2, delay: i * 0.02 },
                        },
                      }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h3>
              </AnimatePresence>
            </div>

            {/* Description */}
            <AnimatePresence mode="wait">
              <motion.p
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-xl text-gray-600 mb-6 leading-relaxed"
              >
                {current.description}
              </motion.p>
            </AnimatePresence>

            {/* Details */}
            <AnimatePresence mode="wait">
              <motion.p
                key={activeIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="text-gray-500 mb-12 leading-relaxed"
              >
                {current.details}
              </motion.p>
            </AnimatePresence>

            {/* Navigation and step indicators */}
            <div className="flex items-center justify-between">
              {/* Step indicators */}
              <div className="flex items-center gap-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeIndex 
                        ? 'bg-blue-900 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-4">
                <motion.button
                  onClick={goPrev}
                  className="group relative w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center overflow-hidden hover:border-blue-900 transition-colors"
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-blue-900"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "0%" }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  />
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="relative z-10 text-blue-900 group-hover:text-white transition-colors"
                  >
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.button>

                <motion.button
                  onClick={goNext}
                  className="group relative w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center overflow-hidden hover:border-blue-900 transition-colors"
                  whileTap={{ scale: 0.95 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-blue-900"
                    initial={{ x: "100%" }}
                    whileHover={{ x: "0%" }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  />
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="relative z-10 text-blue-900 group-hover:text-white transition-colors"
                  >
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom ticker - subtle repeating step names */}
        <div className="absolute -bottom-20 left-0 right-0 overflow-hidden opacity-[0.08] pointer-events-none">
          <motion.div
            className="flex whitespace-nowrap text-4xl md:text-6xl font-bold tracking-tight text-blue-900"
            animate={{ x: [0, -1000] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          >
            {[...Array(8)].map((_, i) => (
              <span key={i} className="mx-8">
                {steps.map((s) => s.title).join(" • ")} •
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}